import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { createContext, PropsWithChildren, useEffect } from 'react'

import { FileType } from '../../_generated/api/export'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useToastContext } from './ToastContext'
import { useUserContext } from './UserContext'
import { useAction } from '../../hooks/action'
import { useProvideContext } from '../../hooks/context'
import Bolt from '../../models/Bolt'
import TableControl from '../../models/TableControl'
import { throwIfResponseNotOk } from '../../tools/api'
import { downloadBlob, downloadText } from '../../tools/dom/download'

export interface ExportContextValue extends DataContextValue {
    exportProject: (projectId?: string) => Promise<void>
    exportBoltConnection: (boltConnectionId?: string) => Promise<void>
    exportBolt: (boltId?: string) => Promise<void>
    exportMeasurement: (boltId?: string, measurementId?: string, dat?: boolean) => Promise<void>
    exportManufactured: (table: TableControl<Bolt>, from: Date, to: Date) => Promise<void>
}

const ExportExtension = { csv: 'csv', dat: 'dat' } as const
type ExportExtension = (typeof ExportExtension)[keyof typeof ExportExtension]

const ExportContext = createContext<ExportContextValue | undefined>(undefined)
const exportFileOptions = { responseType: 'blob' } as AxiosRequestConfig

export default function ({ children }: PropsWithChildren) {
    const { exportApi } = useApiContext()
    const { lazySelectedOrganizationId } = useUserContext()
    const Toast = useToastContext()

    const downloadExportResponse = (
        response: AxiosResponse,
        filename: string,
        extension: ExportExtension = ExportExtension.csv
    ) => {
        throwIfResponseNotOk(response)
        if (response.data instanceof Blob) downloadBlob(filename, response.data, extension)
        else downloadText(filename, response.data.toString(), extension)
    }

    const [projectLoading, projectError, exportProject] = useAction(async (projectId?: string) => {
        if (!projectId) return
        const response = await exportApi.exportProject(
            { projectId, tenantId: lazySelectedOrganizationId },
            exportFileOptions
        )
        downloadExportResponse(response, `${projectId}_project_report`)
    })

    const [boltConnectionLoading, boltConnectionError, exportBoltConnection] = useAction(
        async (boltConnectionId?: string) => {
            if (!boltConnectionId) return
            const response = await exportApi.exportBoltConnection(
                { boltConnectionId, tenantId: lazySelectedOrganizationId },
                exportFileOptions
            )
            downloadExportResponse(response, `${boltConnectionId}_bolt_connection_report`)
        }
    )

    const [boltLoading, boltError, exportBolt] = useAction(async (boltId?: string) => {
        if (!boltId) return
        const response = await exportApi.exportBolt(
            { boltId, tenantId: lazySelectedOrganizationId },
            exportFileOptions
        )
        downloadExportResponse(response, `${boltId}_bolt_report`)
    })

    const [manufacturedLoading, manufacturedError, exportManufactured] = useAction(
        async (table: TableControl<Bolt>, start: Date, end: Date) => {
            const response = await exportApi.exportManufactured(
                {
                    tenantId: lazySelectedOrganizationId,
                    createdAtStart: start.toISOString(),
                    createdAtEnd: end.toISOString(),
                },
                exportFileOptions
            )
            const startString = start.toLocaleDateString().replaceAll('/', '-')
            const endString = end.toLocaleDateString().replaceAll('/', '-')
            downloadExportResponse(response, `manufactured_bolts_${startString}_${endString}`)
        }
    )

    const [measurementLoading, measurementError, exportMeasurement] = useAction(
        async (boltId?: string, measurementId?: string, dat: boolean = false) => {
            if (!(measurementId && boltId)) return
            const fileType = dat ? FileType.Dat : FileType.Csv
            const extension = dat ? ExportExtension.dat : ExportExtension.csv
            const response = await exportApi.exportMeasurement(
                {
                    boltId,
                    measurementId,
                    tenantId: lazySelectedOrganizationId,
                    fileType,
                },
                exportFileOptions
            )
            downloadExportResponse(response, `${measurementId}_measurement_report`, extension)
        }
    )

    const loading = boltLoading || measurementLoading || manufacturedLoading
    const error = boltError || measurementError || manufacturedError

    useEffect(() => {
        if (error) Toast.error(error, 'Bolt error')
    }, [error])

    const value: ExportContextValue = {
        loading,
        error,
        exportProject,
        exportBoltConnection,
        exportBolt,
        exportMeasurement,
        exportManufactured,
    }

    return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>
}

export const useExportContext = () => useProvideContext(ExportContext, 'Export')
